import React from 'react';
import { Check, Radio } from 'lucide-react';
import { useI18n } from '../../context/I18nContext';
import { useTranslations } from '../../hooks/useTranslations';
import { isRightsEndExpired } from '../../utils/rightsDuration';
import Tooltip from '../ui/Tooltip';

interface RightsEndStatusProps {
  endDate: Date | string;
  /** When set (Respecté), show in green */
  checkedAt?: Date | string | null;
  /** Show date text next to the badge (ignored when compact) */
  showDate?: boolean;
  /** Card-friendly: icon + compact date only (same pattern as payment $ + date) */
  compact?: boolean;
  className?: string;
}

/**
 * Rights end status: date (+ optional badge).
 * Shared by project cards (compact) and archived projects table.
 * Compact: Radio/Check + date — meaning from the icon (like $ for payment).
 */
const RightsEndStatus: React.FC<RightsEndStatusProps> = ({
  endDate,
  checkedAt = null,
  showDate = true,
  compact = false,
  className = '',
}) => {
  const { t } = useI18n();
  const { formatDateShort, formatDateCompact } = useTranslations();
  const respected = checkedAt != null && checkedAt !== '';
  const expired = !respected && isRightsEndExpired(endDate);
  const date = endDate instanceof Date ? endDate : new Date(endDate);
  const dateLabelFull = formatDateShort(date);
  const dateLabel = formatDateCompact(date);

  const toneClass = respected
    ? 'text-green-600'
    : expired
      ? 'text-red-600'
      : 'text-gray-600';

  if (compact) {
    const Icon = respected ? Check : Radio;
    return (
      <Tooltip content={t('projects.rightsEndTooltip')}>
        <div className={`inline-flex items-center gap-1 text-xs shrink-0 ${toneClass} ${className}`}>
          <Icon className="w-3.5 h-3.5 shrink-0" />
          <span className="font-medium tabular-nums">{dateLabel}</span>
        </div>
      </Tooltip>
    );
  }

  return (
    <div className={`inline-flex items-center gap-2 min-w-0 ${className}`}>
      {showDate && (
        <span className={`text-xs truncate tabular-nums ${toneClass}`}>{dateLabelFull}</span>
      )}
      <span
        className={`inline-flex items-center font-medium rounded-full px-2 py-0.5 text-xs shrink-0 ${
          respected
            ? 'bg-green-100 text-green-700'
            : expired
              ? 'bg-red-100 text-red-700'
              : 'bg-purple-100 text-purple-700'
        }`}
      >
        {respected
          ? t('projects.rightsRespected')
          : expired
            ? t('projects.rightsExpired')
            : t('projects.rightsInProgress')}
      </span>
    </div>
  );
};

export default RightsEndStatus;
